import {
  EMPRESA_Y_CENTRO_USUARIOS,
  LIST_ENCARGADOS,
  LIST_ENCARGADOS_CON_BODEGAS,
  PRIVILEGIOS_USUARIOS,
} from "@/apollo/queries/epp";
import {
  CREATE_ENCARGADO,
  CREATE_ENCARGADO_CON_BODEGAS,
  DELETE_ENCARGADO,
} from "@/apollo/mutations/epp";
import {
  GET_LIST_CONTACTOS_USUARIOS,
  GET_PAGINACION_CONTACTOS_LISTA,
} from "@/apollo/queries/mail";
import { exMut, exQue } from "@/utils/gql";
import Vue from "vue";

const USUARIOS_POR_PAGINA = 15;

// Rol y bodegas por encargado (al menos una bodega). En false todo funciona como
// antes y no se piden ni se envían. Necesita el backend de "avances cambios epp"
// (CreateEncargado con id_bodegas y ListEncargados con bodegas): con true y un
// backend sin esos campos, la lista de encargados queda vacía.
//
// El rol lo arma el backend con is_supervisor y id_privilegio_fk. Aquí solo se
// elige si es supervisor; id_privilegio_fk es siempre el privilegio del usuario
// (privilegio 1: superadministrador; privilegio 2: administrador local si es
// supervisor y pañolero si no).
export const ENCARGADOS_CON_BODEGAS = true;

// Ids numéricos, sin repetidos ni vacíos
const idsValidos = (ids = []) => [...new Set(ids.map(Number))].filter((id) => id > 0);

// { id_usuario: id_tipo del privilegio }, o null si la consulta falla. Quien no
// trae privilegio queda fuera del objeto.
const privilegiosDe = async (idsUsuarios) => {
  try {
    const usuarios = await exQue(
      PRIVILEGIOS_USUARIOS,
      { id_usuario: idsUsuarios.map(String) },
      true,
    );
    const privilegios = {};
    (usuarios || []).forEach((usuario) => {
      // El backend lo manda como arreglo de un elemento
      const privilegio = Array.isArray(usuario?.privilegio)
        ? usuario.privilegio[0]
        : usuario?.privilegio;
      const idTipo = Number(privilegio?.id_tipo);
      if (idTipo > 0) privilegios[Number(usuario.id_usuario)] = idTipo;
    });
    return privilegios;
  } catch {
    // exQue ya dejó el error en consola
    return null;
  }
};

// listUsersPaginacion manda sociedad y sucursal como arreglo de un elemento
const primero = (valor) => (Array.isArray(valor) ? valor[0] : valor);

// ¿El usuario ({ idSociedad, idSucursal } de encargadosEmpresaCentro) es de esa
// empresa y ese centro de trabajo? Un filtro vacío deja pasar a todos.
const calzaEmpresaCentro = (datos, idSociedad, idSucursal) =>
  Boolean(datos) &&
  (!idSociedad || datos.idSociedad === Number(idSociedad)) &&
  (!idSucursal || datos.idSucursal === Number(idSucursal));

// Mismo formato que PaginacionContactosLista: [{ pagina, ids }]
const enPaginas = (ids) => {
  const paginas = [];
  for (let i = 0; i < ids.length; i += USUARIOS_POR_PAGINA) {
    paginas.push({ pagina: paginas.length + 1, ids: ids.slice(i, i + USUARIOS_POR_PAGINA) });
  }
  return paginas;
};

// Toast desde el store, que no tiene los helpers del mixin global (mismo
// canal que utils/gql.js)
const toast = (message, type) => {
  Vue.$toast.open({ message, type, duration: 5000, position: "top-right" });
};

export const EncargadosState = {
  encargados: [],
  encargadosLoading: false,
  // Búsqueda de usuarios de la empresa para agregar encargados. Usa las mismas
  // consultas que Jefes, con estado propio: Jefes además descarga a todos los
  // usuarios para su selector, y aquí no hace falta.
  encargadosUsuarios: [],
  encargadosUsuariosPaginas: [],
  encargadosBuscando: false,
  // { id_usuario: { empresa, centro } } de los encargados y de los usuarios del
  // buscador (ver cargarEmpresaCentroEncargados). "" si el usuario no tiene
  encargadosEmpresaCentro: {},
};

export const EncargadosMutations = {
  SET_ENCARGADOS(state, encargados) {
    state.encargados = Array.isArray(encargados) ? encargados : [];
  },
  SET_ENCARGADOS_LOADING(state, loading) {
    state.encargadosLoading = loading;
  },
  SET_ENCARGADOS_USUARIOS(state, usuarios) {
    state.encargadosUsuarios = Array.isArray(usuarios) ? usuarios : [];
  },
  SET_ENCARGADOS_USUARIOS_PAGINAS(state, paginas) {
    state.encargadosUsuariosPaginas = Array.isArray(paginas) ? paginas : [];
  },
  SET_ENCARGADOS_BUSCANDO(state, buscando) {
    state.encargadosBuscando = buscando;
  },
  MERGE_ENCARGADOS_EMPRESA_CENTRO(state, datos) {
    state.encargadosEmpresaCentro = { ...state.encargadosEmpresaCentro, ...datos };
  },
};

export const EncargadosActions = {
  async getEncargados({ commit, state, dispatch }) {
    commit("SET_ENCARGADOS_LOADING", true);
    try {
      await dispatch("ensureFormEpp");
      const response = await exQue(
        ENCARGADOS_CON_BODEGAS ? LIST_ENCARGADOS_CON_BODEGAS : LIST_ENCARGADOS,
        { id_formulario: state.formEpp?.id_formulario },
        true,
      );
      commit("SET_ENCARGADOS", response);
      // Sin await: la lista se muestra ya y la empresa y el centro llegan después
      dispatch(
        "cargarEmpresaCentroEncargados",
        (response || []).map((encargado) => encargado?.id_usuario_fk),
      );
      return response;
    } catch (error) {
      console.error(error);
      commit("SET_ENCARGADOS", []);
      return [];
    } finally {
      commit("SET_ENCARGADOS_LOADING", false);
    }
  },

  // Una llamada por usuario: el backend crea encargados de a uno. Con
  // ENCARGADOS_CON_BODEGAS todos reciben las mismas bodegas (al menos una) y el
  // mismo "supervisor", y cada uno su propio privilegio. Devuelve cuántos se agregaron.
  async addEncargados(
    { commit, state, dispatch },
    { idsUsuarios = [], idsBodegas = [], esSupervisor = false } = {},
  ) {
    const ids = idsValidos(idsUsuarios);
    if (!ids.length) return 0;
    const bodegas = idsValidos(idsBodegas);
    if (ENCARGADOS_CON_BODEGAS && !bodegas.length) {
      toast("Selecciona al menos una bodega para los encargados.", "error");
      return 0;
    }

    commit("SET_ENCARGADOS_LOADING", true);
    try {
      await dispatch("ensureFormEpp");
      const idFormulario = state.formEpp?.id_formulario;
      if (!idFormulario) {
        toast("No se pudo cargar el formulario de EPP de la empresa. Recarga la página e inténtalo de nuevo.", "error");
        return 0;
      }

      // Sin privilegio el backend lo inventa (supervisor da superadministrador), así
      // que quien no lo trae no se agrega
      let idsConPrivilegio = ids;
      let privilegios = {};
      if (ENCARGADOS_CON_BODEGAS) {
        privilegios = await privilegiosDe(ids);
        if (!privilegios) {
          toast("No se pudo leer el privilegio de los usuarios. Inténtalo de nuevo.", "error");
          return 0;
        }
        idsConPrivilegio = ids.filter((id) => privilegios[id]);
        const sinPrivilegio = ids.length - idsConPrivilegio.length;
        if (sinPrivilegio) {
          toast(
            sinPrivilegio === 1
              ? "Un usuario no tiene privilegio y no se agregó."
              : `${sinPrivilegio} usuarios no tienen privilegio y no se agregaron.`,
            "error",
          );
        }
      }

      // exMut avisa por su cuenta los errores que devuelve el backend
      const resultados = await Promise.all(
        idsConPrivilegio.map((id_usuario) =>
          ENCARGADOS_CON_BODEGAS
            ? exMut(CREATE_ENCARGADO_CON_BODEGAS, {
                id_usuario,
                id_formulario: idFormulario,
                is_supervisor: Boolean(esSupervisor),
                id_privilegio_fk: privilegios[id_usuario] == 3 ? 2 :  privilegios[id_usuario] ,
                id_bodegas: bodegas,
              })
            : exMut(CREATE_ENCARGADO, { id_usuario, id_formulario: idFormulario }),
        ),
      );
      const agregados = resultados.filter(Boolean).length;

      if (agregados) {
        toast(
          agregados === 1
            ? "Encargado agregado correctamente"
            : `${agregados} encargados agregados correctamente`,
          "success",
        );
        await dispatch("getEncargados");
      }
      return agregados;
    } finally {
      commit("SET_ENCARGADOS_LOADING", false);
    }
  },

  async deleteEncargado({ commit, dispatch }, idEncargado) {
    commit("SET_ENCARGADOS_LOADING", true);
    try {
      const response = await exMut(
        DELETE_ENCARGADO,
        { id_encargado: Number(idEncargado) },
        "Encargado quitado correctamente",
        "No se pudo quitar el encargado",
      );
      if (response) await dispatch("getEncargados");
      return response;
    } finally {
      commit("SET_ENCARGADOS_LOADING", false);
    }
  },

  // Cambia "supervisor" y las bodegas (al menos una) de un encargado. Solo con
  // ENCARGADOS_CON_BODEGAS. Va por CreateEncargado, que edita si la persona ya es
  // encargada, con el privilegio actual del usuario.
  async editarEncargado(
    { commit, state, dispatch },
    { encargado, idsBodegas = [], esSupervisor = false } = {},
  ) {
    const idUsuario = Number(encargado?.id_usuario_fk);
    const bodegas = idsValidos(idsBodegas);
    if (!ENCARGADOS_CON_BODEGAS || !idUsuario || !bodegas.length) return null;

    commit("SET_ENCARGADOS_LOADING", true);
    try {
      await dispatch("ensureFormEpp");
      const privilegios = await privilegiosDe([idUsuario]);
      if (!privilegios?.[idUsuario]) {
        toast("No se pudo leer el privilegio del encargado. Inténtalo de nuevo.", "error");
        return null;
      }

      const response = await exMut(
        CREATE_ENCARGADO_CON_BODEGAS,
        {
          id_usuario: idUsuario,
          id_formulario: state.formEpp?.id_formulario,
          is_supervisor: Boolean(esSupervisor),
          id_privilegio_fk: privilegios[idUsuario] == 3 ? 2 :  privilegios[idUsuario],
          id_bodegas: bodegas,
        },
        "Encargado actualizado correctamente",
        "No se pudo actualizar el encargado",
      );
      if (response) await dispatch("getEncargados");
      return response;
    } finally {
      commit("SET_ENCARGADOS_LOADING", false);
    }
  },

  // Pide las páginas de ids que calzan con el nombre y carga la primera.
  //
  // PaginacionContactosLista no filtra por empresa ni centro de trabajo. Con
  // alguno de los dos, se traen la empresa y el centro de todos los usuarios del
  // resultado (una sola consulta, que queda en encargadosEmpresaCentro), se
  // filtran aquí y se vuelven a armar las páginas.
  async buscarUsuariosEncargados(
    { commit, dispatch, state },
    { nombre = "", idSociedad = null, idSucursal = null } = {},
  ) {
    commit("SET_ENCARGADOS_BUSCANDO", true);
    commit("SET_ENCARGADOS_USUARIOS", []);
    commit("SET_ENCARGADOS_USUARIOS_PAGINAS", []);
    try {
      const paginas = await exQue(
        GET_PAGINACION_CONTACTOS_LISTA,
        {
          limite: USUARIOS_POR_PAGINA,
          nombre: String(nombre || "").trim(),
        },
        true,
      );
      let seguras = Array.isArray(paginas) ? paginas : [];
      if (idSociedad || idSucursal) {
        const ids = idsValidos(seguras.flatMap((pagina) => pagina?.ids || []));
        await dispatch("cargarEmpresaCentroEncargados", ids);
        seguras = enPaginas(
          ids.filter((id) =>
            calzaEmpresaCentro(state.encargadosEmpresaCentro[id], idSociedad, idSucursal),
          ),
        );
      }
      commit("SET_ENCARGADOS_USUARIOS_PAGINAS", seguras);
      if (seguras.length) await dispatch("cargarPaginaUsuariosEncargados", seguras[0]);
      return seguras;
    } catch (error) {
      console.error(error);
      return [];
    } finally {
      commit("SET_ENCARGADOS_BUSCANDO", false);
    }
  },

  async cargarPaginaUsuariosEncargados({ commit, dispatch }, pagina) {
    const ids = Array.isArray(pagina?.ids) ? pagina.ids : [];
    if (!ids.length) {
      commit("SET_ENCARGADOS_USUARIOS", []);
      return [];
    }
    commit("SET_ENCARGADOS_BUSCANDO", true);
    try {
      const usuarios = await exQue(GET_LIST_CONTACTOS_USUARIOS, { idUsuario: ids }, true);
      commit("SET_ENCARGADOS_USUARIOS", usuarios);
      // Sin await, como en getEncargados
      dispatch("cargarEmpresaCentroEncargados", ids);
      return usuarios;
    } catch (error) {
      console.error(error);
      return [];
    } finally {
      commit("SET_ENCARGADOS_BUSCANDO", false);
    }
  },

  // Empresa y centro de trabajo de los usuarios que todavía no los tienen en
  // encargadosEmpresaCentro: nombres para mostrar e ids para filtrar el buscador.
  // Quien no vuelve en la respuesta queda vacío para no pedirlo otra vez. Si la
  // consulta falla no se muestran, y nada más se rompe.
  async cargarEmpresaCentroEncargados({ commit, state }, idsUsuarios = []) {
    const faltan = idsValidos(idsUsuarios).filter((id) => !state.encargadosEmpresaCentro[id]);
    if (!faltan.length) return;
    try {
      const usuarios = await exQue(
        EMPRESA_Y_CENTRO_USUARIOS,
        { id_usuario: faltan.map(String) },
        true,
      );
      const datos = {};
      faltan.forEach((id) => {
        datos[id] = { empresa: "", centro: "", idSociedad: null, idSucursal: null };
      });
      (usuarios || []).forEach((usuario) => {
        const id = Number(usuario?.id_usuario);
        if (!datos[id]) return;
        const sociedad = primero(usuario.sociedad);
        const sucursal = primero(usuario.sucursal);
        datos[id] = {
          empresa: String(sociedad?.nombre_sociedad || "").trim(),
          centro: String(sucursal?.nombre_sucursal || "").trim(),
          idSociedad: Number(sociedad?.id_sociedad) || null,
          idSucursal: Number(sucursal?.id_sucursal) || null,
        };
      });
      commit("MERGE_ENCARGADOS_EMPRESA_CENTRO", datos);
    } catch {
      // exQue ya dejó el error en consola
    }
  },
};
